import React, { FC, useRef, useState, useCallback } from 'react';
import classNames from 'classnames';
import { SearchBarField } from './field/SearchBarField';
import { Connect, PropsFromRedux } from './SearchBarContainer';
import { OutsideFocusHandler } from '../../../components/dropdown/outside-handler/OutsideFocusHandler';
import { useUser } from '../../../user/useUser';
import { doctrineAnalytics } from '../../../utils/analyticsUtils';
import { isTouchDevice } from '../../../utils/domUtils';
import { getRandomSearchPlaceholder } from './searchPlaceholders';
import { SearchDropdown } from './dropdown/SearchDropdown';
import {
  AutocompleteItemTypeKey,
  AutocompleteItemType,
} from './dropdown/searchAutocomplete.types';
import styles from './search_bar.module.scss';

type MetaType = {
  position: number;
  items: AutocompleteItemType[];
  type: AutocompleteItemTypeKey;
};

const getPlaceholder = (placeholder: string) => {
  if (placeholder) return placeholder;
  return isTouchDevice()
    ? 'Recherchez par mots-clés'
    : getRandomSearchPlaceholder();
};

type Props = {
  restrictedDocIds?: string[];
  activateScrollToTop?: boolean;
  allowEmptyQuery?: boolean;
  onClearClicked?: Function;
  placeholder?: string;
} & PropsFromRedux;

export const RawSearchBar: FC<Props> = ({
  allowEmptyQuery = false,
  activateScrollToTop = true,
  enableTypeSelector,
  query,
  onClearClicked = () => {},
  onUserType,
  onQueryPaste,
  submitCurrentQuery,
  followAutocomplete,
  restrictedDocIds,
  placeholder = '',
}) => {
  const [open, setOpen] = useState(false);

  const [autocompleteNav, setAutocompleteNav] = useState('');
  const [placeholderNav, setPlaceholderNav] = useState(
    getPlaceholder(placeholder),
  );
  const { loggedIn } = useUser();
  const inputRef = useRef<HTMLInputElement>(null);
  const meta = useRef<MetaType>();

  const onItemSelected = (newQuery: string, source: string) => {
    if (inputRef.current) {
      inputRef.current.blur();
    }
    followAutocomplete(newQuery, source, restrictedDocIds, allowEmptyQuery);
    setOpen(false);
    setAutocompleteNav('');
  };

  const updateQuery = (newQuery: string, newMeta: MetaType) => {
    setAutocompleteNav(newMeta.type !== 'written_query' ? newQuery : '');
    meta.current = newMeta;
  };

  const manualClose = () => setOpen(false);

  const onSelection = () => {
    if (query.length > 0) {
      setOpen(false);
    } else if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const onChange = ({
    newQuery,
    queryBeforePasting,
    isPasted,
  }: {
    newQuery: string;
    queryBeforePasting: string;
    isPasted: boolean;
  }) => {
    if (activateScrollToTop) window.scrollTo(0, 0);
    if (autocompleteNav !== '' && meta.current) {
      doctrineAnalytics('Search AutocompleteSuggestedQueryCompleted', {
        autocomplete_array: meta.current.items,
        position: meta.current.position,
        selected_query: autocompleteNav,
        previous_query: query,
      });
      meta.current = undefined;
      setAutocompleteNav('');
    }

    if (isPasted) {
      const hasQueryChanged = newQuery !== queryBeforePasting;
      if (hasQueryChanged) {
        const isQueryEnhanced = newQuery.includes(queryBeforePasting);
        onQueryPaste(newQuery, isQueryEnhanced);
      }
    } else {
      onUserType(newQuery);
    }
  };

  const handleClickOutside = useCallback(() => {
    if (autocompleteNav !== '') {
      onUserType(autocompleteNav);
      setAutocompleteNav('');
    }
    if (open) {
      setOpen((previous) => !previous);
      setPlaceholderNav(getPlaceholder(placeholder));
    }
    if (inputRef.current) {
      inputRef.current.blur();
    }
  }, [autocompleteNav, inputRef, open, onUserType, placeholder]);

  const onClear = () => {
    doctrineAnalytics('Search DeleteQueryClicked');
    setAutocompleteNav('');
    onUserType('');
    onClearClicked();
  };

  const onFocus = () => {
    setOpen(true);
    setPlaceholderNav('');
  };

  const onSubmit = () => {
    submitCurrentQuery(query, restrictedDocIds, allowEmptyQuery);
    setOpen(false);
    setPlaceholderNav(getPlaceholder(placeholder));
    window.scrollTo(0, 0);
    if (inputRef.current) {
      inputRef.current.blur();
    }
  };

  const onClose = () => {
    setOpen(false);
    setPlaceholderNav(getPlaceholder(placeholder));
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.section}>
        <OutsideFocusHandler
          className={classNames(styles.group, { [styles.expanded]: open })}
          onOutsideClick={handleClickOutside}
        >
          <SearchBarField
            forwardRef={inputRef}
            placeholder={placeholderNav}
            query={autocompleteNav || query}
            isOpen={open}
            onChange={onChange}
            onClear={onClear}
            onFocus={onFocus}
            onSubmit={onSubmit}
            onClose={onClose}
            allowEmptyQuery={allowEmptyQuery}
          />
          {open && (
            <SearchDropdown
              query={query}
              isLoggedIn={loggedIn}
              onItemSelected={onItemSelected}
              updateQuery={updateQuery}
              manualClose={manualClose}
              onSelection={onSelection}
              enableTypeSelector={enableTypeSelector}
              allowEmptyQuery={allowEmptyQuery}
            />
          )}
        </OutsideFocusHandler>
      </div>
    </div>
  );
};

export const SearchBar = Connect(RawSearchBar);
